import React from 'react';
/** Co-located in practice/ domain (CL-1 s64). */
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { DjayAvatar, DjayEmotion, useDjaySelection } from '../../../mascot';
interface DjayReactionHeaderProps {
  isCorrect: boolean;
  motivationalText: string;
  xpGained: number;
  mode?: 'full' | 'compact';
  streak?: number;
  distractorExplanation?: string;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
}
const CONFETTI_DOTS: Array<{
  top: string;
  left?: string;
  right?: string;
  color: string;
  size: string;
  delay: number;
}> = [{
  top: '10%',
  left: '12%',
  color: 'bg-amber-400',
  size: 'w-2 h-2',
  delay: 0.1
}, {
  top: '18%',
  right: '15%',
  color: 'bg-orange-300',
  size: 'w-1.5 h-1.5',
  delay: 0.15
}, {
  top: '65%',
  left: '10%',
  color: 'bg-yellow-400',
  size: 'w-2 h-2',
  delay: 0.2
}, {
  top: '60%',
  right: '12%',
  color: 'bg-rose-300',
  size: 'w-1.5 h-1.5',
  delay: 0.25
}, {
  top: '35%',
  left: '8%',
  color: 'bg-indigo-300',
  size: 'w-1.5 h-1.5',
  delay: 0.18
}];
export function DjayReactionHeader({
  isCorrect,
  motivationalText,
  xpGained,
  mode = 'full',
  streak = 0,
  distractorExplanation,
  levelUp
}: DjayReactionHeaderProps) {
  if (mode === 'compact') {
    return <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-6622">
        {isCorrect ? <Check className="w-5 h-5" data-id="element-6623" /> : <X className="w-5 h-5" data-id="element-6624" />}
      </div>;
  }
  const {
    selectedVariant
  } = useDjaySelection();
  const showDistractor = !isCorrect && !!distractorExplanation;
  const bubbleText = levelUp && isCorrect ? `Wauw, level ${levelUp.level}! 🎉` : showDistractor ? distractorExplanation : motivationalText;
  const djayEmotion: DjayEmotion = isCorrect ? levelUp ? 'proud' : streak >= 5 ? 'excited' : 'happy' : showDistractor ? 'thinking' : 'pointing';
  return <div className="relative flex flex-col items-center justify-center pt-4 pb-3" data-id="element-6625">
      {isCorrect && CONFETTI_DOTS.map((dot, i) => <motion.div key={i} className={`absolute rounded-full ${dot.color} ${dot.size} pointer-events-none z-0`} style={{
      top: dot.top,
      left: dot.left,
      right: dot.right
    }} initial={{
      opacity: 0,
      scale: 0,
      y: 6
    }} animate={{
      opacity: [0, 0.9, 0.9, 0],
      scale: [0, 1, 1, 0.7],
      y: [6, -4, -10, -16]
    }} transition={{
      delay: dot.delay,
      duration: 1.4,
      ease: 'easeOut'
    }} data-id="element-6626" />)}

      <motion.div className="relative z-10" initial={{
      scale: 0.85,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1,
      y: [0, -3, 0]
    }} transition={{
      scale: {
        type: 'spring',
        stiffness: 280,
        damping: 22
      },
      opacity: {
        duration: 0.3
      },
      y: {
        delay: 0.3,
        duration: 2,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      }
    }} data-id="element-6627">
        <DjayAvatar size="xl" emotion={djayEmotion} variant={selectedVariant} animate={true} data-id="element-6628" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 6,
      scale: 0.92
    }} animate={{
      opacity: 1,
      y: 0,
      scale: [1, 1.01, 1]
    }} transition={{
      opacity: {
        delay: 0.1,
        duration: 0.25
      },
      y: {
        delay: 0.1,
        duration: 0.25
      },
      scale: {
        delay: 0.4,
        duration: 3,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      }
    }} className="relative mt-2 z-20" data-id="element-6629">
        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-t border-l border-slate-200/70" data-id="element-6630" />
        <div className={`relative bg-white/95 backdrop-blur-sm px-5 py-3 rounded-2xl shadow-md shadow-slate-200/50 border border-slate-200/70 text-center ${showDistractor ? 'max-w-[340px]' : 'max-w-[300px]'}`} data-id="element-6631">
          {showDistractor && <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500 mb-1" data-id="element-6632">
              Waarom dit niet klopt
            </p>}
          <p className={`font-bold tracking-tight ${isCorrect ? 'text-emerald-700 text-base' : showDistractor ? 'text-slate-700 text-sm leading-relaxed' : 'text-slate-700 text-base'}`} data-id="element-6633">
            {bubbleText}
          </p>
        </div>
      </motion.div>
    </div>;
}